import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Syncs the current user to Brevo contacts on first login/signup.
 * Also sends a welcome marketing email for new users.
 */
export const useBrevoSync = () => {
  const { user } = useAuth();
  const syncedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.email || syncedRef.current === user.id) return;
    syncedRef.current = user.id;

    const sync = async () => {
      try {
        // Get profile data for attributes
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name, first_name, last_name, phone, city")
          .eq("user_id", user.id)
          .single();

        // Sync contact to Brevo
        await supabase.functions.invoke("brevo-api", {
          body: {
            action: "sync_contact",
            email: user.email,
            attributes: {
              FIRSTNAME: profile?.first_name || profile?.display_name || "",
              LASTNAME: profile?.last_name || "",
              SMS: profile?.phone || "",
              CITY: profile?.city || "",
              SIGNUP_DATE: new Date().toISOString().split("T")[0],
            },
          },
        });

        // Check if this is a new user (created in last 5 minutes)
        const createdAt = new Date(user.created_at || "");
        const isNew = Date.now() - createdAt.getTime() < 5 * 60 * 1000;

        if (isNew) {
          // Enqueue the full welcome automation flow (welcome → how_it_works → almost_there → reengagement)
          await supabase.functions.invoke("process-marketing-queue", {
            body: {
              action: "enqueue_flow",
              user_id: user.id,
              flow_type: "welcome",
            },
          });
        }
      } catch (err) {
        console.error("[BrevoSync] Error:", err);
      }
    };

    void sync();
  }, [user?.id, user?.email]);
};
